(function cartInventoryControllerFactory(root) {
  'use strict';

  function buildCartApiPath(path) {
    const routeRoot = root.Shopify?.routes?.root || '/';
    const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    const normalizedPath = String(path || '').replace(/^\//, '');

    return `${normalizedRoot}${normalizedPath}`;
  }

  function normalizeQuantity(value) {
    const quantity = Number(value);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      return 0;
    }

    return Math.floor(quantity);
  }

  function normalizeMaximum(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return Math.max(normalizeQuantity(value), 0);
  }

  function getCartVariantQuantity(cart, variantId) {
    const normalizedVariantId = Number(variantId);

    if (!Number.isFinite(normalizedVariantId) || !Array.isArray(cart?.items)) {
      return 0;
    }

    return cart.items.reduce((total, item) => {
      const itemVariantId = Number(item?.variant_id ?? item?.id);

      if (itemVariantId !== normalizedVariantId) {
        return total;
      }

      return total + normalizeQuantity(item?.quantity);
    }, 0);
  }

  function getRemainingQuantity(maximum, cart, variantId, reservedQuantity = 0) {
    const normalizedMaximum = normalizeMaximum(maximum);

    if (normalizedMaximum === null) {
      return null;
    }

    const quantityInCart = getCartVariantQuantity(cart, variantId);

    return Math.max(normalizedMaximum - quantityInCart - normalizeQuantity(reservedQuantity), 0);
  }

  function getAddableQuantity(item, cart, reservedQuantity = 0) {
    const requestedQuantity = normalizeQuantity(item?.quantity);
    const minimum = Math.max(normalizeQuantity(item?.minimum), 1);
    const increment = Math.max(normalizeQuantity(item?.increment), 1);
    const quantityInCart = getCartVariantQuantity(cart, item?.id) + normalizeQuantity(reservedQuantity);
    const remainingQuantity = getRemainingQuantity(
      item?.maxQuantity,
      cart,
      item?.id,
      reservedQuantity,
    );

    const cappedQuantity = remainingQuantity === null
      ? requestedQuantity
      : Math.min(requestedQuantity, remainingQuantity);

    if (quantityInCart > 0) {
      return Math.floor(cappedQuantity / increment) * increment;
    }

    if (cappedQuantity < minimum) {
      return 0;
    }

    return minimum + (Math.floor((cappedQuantity - minimum) / increment) * increment);
  }

  function clampLineQuantity(value, maximum, fallback = 1) {
    const requestedQuantity = normalizeQuantity(value) || normalizeQuantity(fallback);
    const normalizedMaximum = normalizeMaximum(maximum);

    if (normalizedMaximum === null) {
      return requestedQuantity;
    }

    return Math.min(requestedQuantity, normalizedMaximum);
  }

  function buildAvailableItems(items, cart) {
    const reservedByVariant = new Map();

    return (Array.isArray(items) ? items : []).reduce((availableItems, item) => {
      const id = Number(item?.id);

      if (!Number.isFinite(id) || id <= 0) {
        return availableItems;
      }

      const reservedQuantity = reservedByVariant.get(id) || 0;
      const quantity = getAddableQuantity(item, cart, reservedQuantity);

      if (quantity <= 0) {
        return availableItems;
      }

      reservedByVariant.set(id, reservedQuantity + quantity);
      const availableItem = { id, quantity };

      if (item?.properties && typeof item.properties === 'object') {
        availableItem.properties = { ...item.properties };
      }

      if (item?.sellingPlan) {
        availableItem.selling_plan = Number(item.sellingPlan);
      }

      availableItems.push(availableItem);

      return availableItems;
    }, []);
  }

  function parseCartError(payload, fallback = '') {
    if (typeof payload === 'string') {
      return payload;
    }

    return payload?.description || payload?.message || fallback;
  }

  class CartRequestError extends Error {
    constructor(message, options = {}) {
      super(message);
      this.name = 'CartRequestError';
      this.status = Number(options.status) || 0;
      this.payload = options.payload || {};
      this.cart = options.cart || null;
      this.path = options.path || '';
    }
  }

  async function requestCartJson(path, options = {}) {
    if (!root.fetch) {
      return {};
    }

    const response = await root.fetch(buildCartApiPath(path), options);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new CartRequestError(parseCartError(data, response.statusText), {
        status: response.status,
        payload: data,
        path,
      });
    }

    return data;
  }

  let cachedCartPromise = null;
  let cartCacheVersion = 0;

  function cacheCart(cart) {
    if (cart && Array.isArray(cart.items)) {
      cartCacheVersion += 1;
      cachedCartPromise = Promise.resolve(cart);
    }

    return cart;
  }

  function fetchCart(options = {}) {
    if (!options.fresh && cachedCartPromise) {
      return cachedCartPromise;
    }

    const requestVersion = cartCacheVersion + 1;
    cartCacheVersion = requestVersion;
    const request = requestCartJson('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    cachedCartPromise = request
      .then((cart) => {
        if (requestVersion === cartCacheVersion && cart && Array.isArray(cart.items)) {
          cachedCartPromise = Promise.resolve(cart);
        }

        return cart;
      })
      .catch((error) => {
        if (requestVersion === cartCacheVersion) {
          cachedCartPromise = null;
        }
        throw error;
      });

    return cachedCartPromise;
  }

  function getAddedItems(requestedItems, cartBefore, cartAfter) {
    const requestedByVariant = new Map();

    (Array.isArray(requestedItems) ? requestedItems : []).forEach((item) => {
      const id = Number(item?.id);
      if (!Number.isFinite(id) || id <= 0) {
        return;
      }

      requestedByVariant.set(id, (requestedByVariant.get(id) || 0) + normalizeQuantity(item?.quantity));
    });

    return Array.from(requestedByVariant.entries()).reduce((addedItems, [id, requestedQuantity]) => {
      const quantityBefore = getCartVariantQuantity(cartBefore, id);
      const quantityAfter = getCartVariantQuantity(cartAfter, id);
      const addedQuantity = Math.min(Math.max(quantityAfter - quantityBefore, 0), requestedQuantity);

      if (addedQuantity > 0) {
        addedItems.push({ id, quantity: addedQuantity });
      }

      return addedItems;
    }, []);
  }

  const CART_LINE_PRIORITY_STORAGE_KEY = 'justfishything-cart-line-priority-v1';

  function getStoredLinePriority() {
    try {
      const stored = JSON.parse(root.localStorage?.getItem(CART_LINE_PRIORITY_STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter((key) => typeof key === 'string' && key) : [];
    } catch (_error) {
      return [];
    }
  }

  function markCartLinesUpdated(lineKeys) {
    const normalizedKeys = (Array.isArray(lineKeys) ? lineKeys : [])
      .map((key) => String(key || ''))
      .filter(Boolean);

    if (!normalizedKeys.length) {
      return getStoredLinePriority();
    }

    const updatedKeys = new Set(normalizedKeys);
    const priority = [
      ...normalizedKeys,
      ...getStoredLinePriority().filter((key) => !updatedKeys.has(key)),
    ].slice(0, 50);

    try {
      root.localStorage?.setItem(CART_LINE_PRIORITY_STORAGE_KEY, JSON.stringify(priority));
    } catch (_error) {
      // Storage can be unavailable in private browsing; cart mutations still work normally.
    }

    return priority;
  }

  function getChangedLineKeys(requestedItems, cartBefore, cartAfter) {
    const requestedVariantIds = new Set(
      (Array.isArray(requestedItems) ? requestedItems : [])
        .map((item) => Number(item?.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    const beforeByKey = new Map(
      (Array.isArray(cartBefore?.items) ? cartBefore.items : [])
        .map((item) => [String(item?.key || ''), normalizeQuantity(item?.quantity)]),
    );

    return (Array.isArray(cartAfter?.items) ? cartAfter.items : [])
      .filter((item) => requestedVariantIds.has(Number(item?.variant_id ?? item?.id)))
      .filter((item) => normalizeQuantity(item?.quantity) > (beforeByKey.get(String(item?.key || '')) || 0))
      .map((item) => String(item?.key || ''))
      .filter(Boolean);
  }

  function prioritizeCartLineItems(scope = root.document) {
    if (!scope || typeof scope.querySelectorAll !== 'function') {
      return 0;
    }

    const priority = getStoredLinePriority();
    if (!priority.length) {
      return 0;
    }

    const priorityIndex = new Map(priority.map((key, index) => [key, index]));
    let reorderedItems = 0;

    scope.querySelectorAll('[data-cart-items]').forEach((container) => {
      const items = Array.from(container.children).filter((item) => item.matches?.('[data-cart-line-key]'));
      const originalIndex = new Map(items.map((item, index) => [item, index]));

      items.sort((left, right) => {
        const leftPriority = priorityIndex.get(left.dataset.cartLineKey);
        const rightPriority = priorityIndex.get(right.dataset.cartLineKey);
        const leftRank = leftPriority === undefined ? Number.MAX_SAFE_INTEGER : leftPriority;
        const rightRank = rightPriority === undefined ? Number.MAX_SAFE_INTEGER : rightPriority;

        return leftRank - rightRank || originalIndex.get(left) - originalIndex.get(right);
      });

      items.forEach((item) => container.appendChild(item));
      reorderedItems += items.length;
    });

    return reorderedItems;
  }

  function getTotalQuantity(items) {
    return (Array.isArray(items) ? items : []).reduce(
      (total, item) => total + normalizeQuantity(item?.quantity),
      0,
    );
  }

  function itemsMatchExactly(requestedItems, availableItems) {
    const requested = Array.isArray(requestedItems) ? requestedItems : [];
    const available = Array.isArray(availableItems) ? availableItems : [];

    if (!requested.length || requested.length !== available.length) {
      return false;
    }

    return requested.every((item, index) => (
      Number(item?.id) === Number(available[index]?.id)
      && normalizeQuantity(item?.quantity) === normalizeQuantity(available[index]?.quantity)
    ));
  }

  function addedItemsMatchExactly(requestedItems, addedItems) {
    const requestedByVariant = new Map();
    const addedByVariant = new Map();

    (Array.isArray(requestedItems) ? requestedItems : []).forEach((item) => {
      const id = Number(item?.id);
      if (Number.isFinite(id) && id > 0) {
        requestedByVariant.set(id, (requestedByVariant.get(id) || 0) + normalizeQuantity(item?.quantity));
      }
    });

    (Array.isArray(addedItems) ? addedItems : []).forEach((item) => {
      const id = Number(item?.id);
      if (Number.isFinite(id) && id > 0) {
        addedByVariant.set(id, (addedByVariant.get(id) || 0) + normalizeQuantity(item?.quantity));
      }
    });

    if (!requestedByVariant.size || requestedByVariant.size !== addedByVariant.size) {
      return false;
    }

    return Array.from(requestedByVariant.entries()).every(
      ([id, quantity]) => addedByVariant.get(id) === quantity,
    );
  }

  function getCartDiscountCodes(cart) {
    return (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
      .map((discount) => String(discount?.code || '').trim())
      .filter(Boolean)
      .join(',');
  }

  async function rollbackCartMutation(cartBefore, cartAfter, updatedLineKeys) {
    const beforeQuantityByKey = new Map(
      (Array.isArray(cartBefore?.items) ? cartBefore.items : [])
        .map((item) => [String(item?.key || ''), normalizeQuantity(item?.quantity)]),
    );
    const afterKeys = new Set(
      (Array.isArray(cartAfter?.items) ? cartAfter.items : [])
        .map((item) => String(item?.key || ''))
        .filter(Boolean),
    );
    const updates = {};

    (Array.isArray(updatedLineKeys) ? updatedLineKeys : []).forEach((lineKey) => {
      const key = String(lineKey || '');
      if (key && afterKeys.has(key)) {
        updates[key] = beforeQuantityByKey.get(key) || 0;
      }
    });

    if (!Object.keys(updates).length) {
      throw new CartRequestError('The cart change could not be rolled back safely.', {
        status: 409,
        cart: cartAfter,
        path: '/cart/update.js',
      });
    }

    const restoredCart = await requestCartJson('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        updates,
        discount: getCartDiscountCodes(cartBefore),
      }),
    });

    if (restoredCart && Array.isArray(restoredCart.items)) {
      return cacheCart(restoredCart);
    }

    return fetchCart({ fresh: true });
  }

  function waitForConflictRetry(attempt) {
    const timeout = root.setTimeout || setTimeout;
    const delay = 40 * (2 ** Math.max(Number(attempt) || 0, 0));

    return new Promise((resolve) => timeout(resolve, delay));
  }

  function withCartMutationLock(callback) {
    if (root.navigator?.locks && typeof root.navigator.locks.request === 'function') {
      return root.navigator.locks.request('justfishything-cart-mutation', { mode: 'exclusive' }, callback);
    }

    return callback();
  }

  let pendingMutation = Promise.resolve();

  function queueCartMutation(callback) {
    const result = pendingMutation
      .catch(() => {})
      .then(() => withCartMutationLock(callback));
    pendingMutation = result.catch(() => {});

    return result;
  }

  const MAX_CONFLICT_RETRIES = 2;

  async function reconcileCart(fallbackCart) {
    try {
      return await fetchCart({ fresh: true });
    } catch (_error) {
      return fallbackCart || { items: [] };
    }
  }

  async function performAddItems(items, attempt = 0) {
    const cartBeforeAdd = await fetchCart({ fresh: true });
    const availableItems = buildAvailableItems(items, cartBeforeAdd);

    if (!availableItems.length) {
      return {
        cart: cartBeforeAdd,
        addedItems: [],
        updatedLineKeys: [],
        requestedItems: [],
        partial: false,
        message: '',
        status: 200,
      };
    }

    try {
      await requestCartJson('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: availableItems }),
      });

      const cart = await fetchCart({ fresh: true });
      const updatedLineKeys = getChangedLineKeys(availableItems, cartBeforeAdd, cart);
      markCartLinesUpdated(updatedLineKeys);

      return {
        cart,
        addedItems: getAddedItems(availableItems, cartBeforeAdd, cart),
        updatedLineKeys,
        requestedItems: availableItems,
        partial: false,
        message: '',
        status: 200,
      };
    } catch (error) {
      const cart = await reconcileCart(cartBeforeAdd);
      const addedItems = getAddedItems(availableItems, cartBeforeAdd, cart);

      if (addedItems.length) {
        const updatedLineKeys = getChangedLineKeys(availableItems, cartBeforeAdd, cart);
        markCartLinesUpdated(updatedLineKeys);
        return {
          cart,
          addedItems,
          updatedLineKeys,
          requestedItems: availableItems,
          partial: getTotalQuantity(addedItems) < getTotalQuantity(availableItems),
          message: parseCartError(error),
          status: Number(error?.status) || 0,
        };
      }

      if (Number(error?.status) === 409 && attempt < MAX_CONFLICT_RETRIES) {
        await waitForConflictRetry(attempt);
        return performAddItems(items, attempt + 1);
      }

      error.cart = cart;
      throw error;
    }
  }

  function addItems(items) {
    return queueCartMutation(() => performAddItems(items));
  }

  async function performAddItemsExact(itemsOrFactory, options = {}, attempt = 0) {
    const cartBeforeAdd = await fetchCart({ fresh: true });
    const requestedItems = typeof itemsOrFactory === 'function'
      ? await itemsOrFactory(cartBeforeAdd)
      : itemsOrFactory;
    const availableItems = buildAvailableItems(requestedItems, cartBeforeAdd);
    const unavailableMessage = String(
      options.unavailableMessage || 'One or more requested items are no longer available.',
    );

    if (!itemsMatchExactly(requestedItems, availableItems)) {
      throw new CartRequestError(unavailableMessage, {
        status: 422,
        cart: cartBeforeAdd,
        path: '/cart/add.js',
      });
    }

    let cartAfterAdd;
    let addedItems = [];
    let updatedLineKeys = [];

    try {
      await requestCartJson('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: availableItems }),
      });

      cartAfterAdd = await fetchCart({ fresh: true });
      addedItems = getAddedItems(availableItems, cartBeforeAdd, cartAfterAdd);
      updatedLineKeys = getChangedLineKeys(availableItems, cartBeforeAdd, cartAfterAdd);
    } catch (error) {
      cartAfterAdd = await reconcileCart(cartBeforeAdd);
      addedItems = getAddedItems(availableItems, cartBeforeAdd, cartAfterAdd);
      updatedLineKeys = getChangedLineKeys(availableItems, cartBeforeAdd, cartAfterAdd);

      if (addedItems.length) {
        try {
          error.cart = await rollbackCartMutation(cartBeforeAdd, cartAfterAdd, updatedLineKeys);
          error.rolledBack = true;
        } catch (rollbackError) {
          error.cart = rollbackError?.cart || cartAfterAdd;
          error.rollbackError = rollbackError;
        }
        throw error;
      }

      if (Number(error?.status) === 409 && attempt < MAX_CONFLICT_RETRIES) {
        await waitForConflictRetry(attempt);
        return performAddItemsExact(itemsOrFactory, options, attempt + 1);
      }

      error.cart = cartAfterAdd;
      throw error;
    }

    if (!addedItemsMatchExactly(availableItems, addedItems)) {
      const exactAddError = new CartRequestError(unavailableMessage, {
        status: 422,
        cart: cartAfterAdd,
        path: '/cart/add.js',
      });

      try {
        exactAddError.cart = await rollbackCartMutation(cartBeforeAdd, cartAfterAdd, updatedLineKeys);
        exactAddError.rolledBack = true;
      } catch (rollbackError) {
        exactAddError.cart = rollbackError?.cart || cartAfterAdd;
        exactAddError.rollbackError = rollbackError;
      }

      throw exactAddError;
    }

    const result = {
      cart: cartAfterAdd,
      addedItems,
      updatedLineKeys,
      requestedItems: availableItems,
      partial: false,
      message: '',
      status: 200,
    };

    if (typeof options.finalize === 'function') {
      try {
        const finalizedCart = await options.finalize(result, cartBeforeAdd);
        if (!finalizedCart || !Array.isArray(finalizedCart.items)) {
          throw new CartRequestError('The cart update could not be verified.', {
            status: 502,
            cart: cartAfterAdd,
          });
        }

        result.cart = cacheCart(finalizedCart);
      } catch (error) {
        try {
          error.cart = await rollbackCartMutation(cartBeforeAdd, cartAfterAdd, updatedLineKeys);
          error.rolledBack = true;
        } catch (rollbackError) {
          error.cart = rollbackError?.cart || cartAfterAdd;
          error.rollbackError = rollbackError;
        }
        throw error;
      }
    }

    markCartLinesUpdated(updatedLineKeys);
    return result;
  }

  function addItemsExact(itemsOrFactory, options = {}) {
    return queueCartMutation(() => performAddItemsExact(itemsOrFactory, options));
  }

  function addVariant(item, options = {}) {
    if (typeof options.finalize === 'function') {
      return addItemsExact([item], options);
    }

    return addItems([item]);
  }

  function getCartLineQuantity(cart, lineKey) {
    const line = (Array.isArray(cart?.items) ? cart.items : []).find((item) => item?.key === lineKey);
    return line ? normalizeQuantity(line.quantity) : 0;
  }

  async function performChangeLine(lineIdentifier, quantity, attempt = 0) {
    const numericLine = Number(lineIdentifier);
    const normalizedLine = Number.isInteger(numericLine) && numericLine > 0 ? numericLine : null;
    const normalizedQuantity = normalizeQuantity(quantity);
    const cartBeforeChange = await fetchCart({ fresh: true });
    const lineKey = normalizedLine === null
      ? String(lineIdentifier || '')
      : cartBeforeChange.items?.[normalizedLine - 1]?.key || '';
    const quantityBefore = getCartLineQuantity(cartBeforeChange, lineKey);
    const changePayload = lineKey
      ? { id: lineKey, quantity: normalizedQuantity }
      : { line: normalizedLine || 1, quantity: normalizedQuantity };

    try {
      const cart = await requestCartJson('/cart/change.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(changePayload),
      });

      cacheCart(cart);
      const updatedLineKeys = normalizedQuantity > 0 && lineKey ? [lineKey] : [];
      markCartLinesUpdated(updatedLineKeys);
      return {
        cart,
        updatedLineKeys,
        partial: false,
        message: '',
        status: 200,
      };
    } catch (error) {
      const cart = await reconcileCart(cartBeforeChange);
      const quantityAfter = getCartLineQuantity(cart, lineKey);

      if (lineKey && quantityAfter !== quantityBefore) {
        const updatedLineKeys = quantityAfter > 0 ? [lineKey] : [];
        markCartLinesUpdated(updatedLineKeys);
        return {
          cart,
          updatedLineKeys,
          partial: quantityAfter !== normalizedQuantity,
          message: parseCartError(error),
          status: Number(error?.status) || 0,
        };
      }

      if (Number(error?.status) === 409 && attempt < MAX_CONFLICT_RETRIES) {
        await waitForConflictRetry(attempt);
        return performChangeLine(lineIdentifier, quantity, attempt + 1);
      }

      error.cart = cart;
      throw error;
    }
  }

  function changeLine(lineIdentifier, quantity) {
    return queueCartMutation(() => performChangeLine(lineIdentifier, quantity));
  }

  root.cartInventory = {
    addItems,
    addItemsExact,
    addVariant,
    buildAvailableItems,
    cacheCart,
    changeLine,
    clampLineQuantity,
    fetchCart,
    getCartVariantQuantity,
    getRemainingQuantity,
    markCartLinesUpdated,
    prioritizeCartLineItems,
  };

  if (root.document && typeof root.document.addEventListener === 'function') {
    root.document.addEventListener('cart:updated', (event) => {
      cacheCart(event?.detail?.cart);
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildAvailableItems,
      buildCartApiPath,
      clampLineQuantity,
      addedItemsMatchExactly,
      getAddableQuantity,
      getAddedItems,
      getChangedLineKeys,
      getCartVariantQuantity,
      getRemainingQuantity,
      getStoredLinePriority,
      itemsMatchExactly,
      markCartLinesUpdated,
      normalizeMaximum,
      normalizeQuantity,
      prioritizeCartLineItems,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
